/**
 * Animated earth + sun scene for Slide 6 (Erdrotation).
 * Sun on the left lights the earth; continents rotate so each one cycles
 * Tag → Nacht → Tag — and the dark side stays *visible* (continents dim
 * but don't disappear) so a kid sees the rotation, not just a static globe.
 */
export function EarthSunScene() {
  // Geometry
  const SUN_X = 110;
  const SUN_Y = 180;
  const EARTH_X = 430;
  const EARTH_Y = 180;
  const R = 130;

  // 8 stylised continents — paths are RELATIVE to (0, 0); we translate the
  // whole group to earth center, then rotate around 0,0. So shapes orbit
  // properly and every shape passes through every position.
  const continents: { d: string; fill: string }[] = [
    { d: "M -70 -50 q 18 -8 30 4 q 8 14 -2 26 q -10 12 -28 8 q -16 -4 -14 -22 q 0 -10 14 -16 z", fill: "#2a4a99" },
    { d: "M  10 -70 q 22 -2 28 14 q 4 16 -10 22 q -18 6 -28 -8 q -8 -14 0 -22 q 4 -6 10 -6 z", fill: "#4d6bb3" },
    { d: "M  60 -20 q 22 4 24 24 q 0 18 -16 22 q -18 4 -22 -10 q -2 -14 4 -28 q 4 -10 10 -8 z", fill: "#2a4a99" },
    { d: "M -10  20 q 22 -2 32 14 q 8 16 -2 30 q -14 18 -32 8 q -16 -10 -10 -28 q 4 -16 12 -24 z", fill: "#4d6bb3" },
    { d: "M  40  60 q 18 0 22 14 q 2 16 -14 18 q -18 2 -22 -10 q -2 -14 4 -16 q 6 -8 10 -6 z", fill: "#2a4a99" },
    { d: "M -50  60 q 16 -4 24 8 q 6 12 -4 22 q -12 10 -22 4 q -10 -6 -8 -18 q 2 -12 10 -16 z", fill: "#4d6bb3" },
    { d: "M -90  10 q 14 -4 18 8 q 4 14 -8 18 q -12 4 -16 -6 q -4 -10 0 -16 q 2 -6 6 -4 z", fill: "#2a4a99" },
    { d: "M -90 -110 q 8 -4 14 4 q 4 8 -4 12 q -10 4 -12 -4 q -2 -8 2 -12 z", fill: "#4dafff" },
  ];

  // 7 city-light dots on the night side
  const lights = [
    { dx: 25, dy: -30, delay: "0s" },
    { dx: 50, dy: 10, delay: "1.2s" },
    { dx: 70, dy: -10, delay: "0.6s" },
    { dx: 60, dy: 50, delay: "2s" },
    { dx: 40, dy: -55, delay: "0.3s" },
    { dx: 80, dy: 30, delay: "1.6s" },
    { dx: 35, dy: 65, delay: "0.9s" },
  ];

  return (
    <svg
      viewBox="0 0 600 360"
      width="100%"
      height="100%"
      role="img"
      aria-label="Animation: Sonne strahlt von links auf die rotierende Erde, eine Hälfte ist Tag, die andere Nacht."
      preserveAspectRatio="xMidYMid meet"
      style={{ display: "block" }}
    >
      <defs>
        <radialGradient id="sunCore" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFE9A6" />
          <stop offset="55%" stopColor="#F5C242" />
          <stop offset="100%" stopColor="#F39200" />
        </radialGradient>
        <radialGradient id="sunHalo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(255, 213, 90, 0.65)" />
          <stop offset="55%" stopColor="rgba(255, 168, 28, 0.18)" />
          <stop offset="100%" stopColor="rgba(255, 168, 28, 0)" />
        </radialGradient>

        <radialGradient id="ocean" cx="35%" cy="40%" r="70%">
          <stop offset="0%" stopColor="#1E7AC9" />
          <stop offset="60%" stopColor="#0A4FA0" />
          <stop offset="100%" stopColor="#062F66" />
        </radialGradient>

        {/* Day/Night terminator — sun is LEFT, dark side RIGHT.
            Softer max opacity (0.55) so continents stay visible while passing through. */}
        <linearGradient id="terminator" x1="0%" y1="50%" x2="100%" y2="50%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.20)" />
          <stop offset="42%" stopColor="rgba(255,255,255,0.02)" />
          <stop offset="55%" stopColor="rgba(0,15,40,0.30)" />
          <stop offset="100%" stopColor="rgba(0,10,30,0.55)" />
        </linearGradient>

        <radialGradient id="dayGlow" cx="28%" cy="32%" r="45%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.30)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </radialGradient>

        {/* Right-half-of-earth mask for night-side city lights */}
        <clipPath id="nightHalf">
          <path d={`M ${EARTH_X} ${EARTH_Y - R} A ${R} ${R} 0 0 1 ${EARTH_X} ${EARTH_Y + R} L ${EARTH_X} ${EARTH_Y - R} Z`} />
        </clipPath>

        <clipPath id="earthClip">
          <circle cx={EARTH_X} cy={EARTH_Y} r={R} />
        </clipPath>
      </defs>

      {/* ── Sun ───────────────────────────────────── */}
      <circle cx={SUN_X} cy={SUN_Y} r="120" fill="url(#sunHalo)">
        <animate attributeName="opacity" values="0.85;1;0.85" dur="6s" repeatCount="indefinite" />
      </circle>
      <circle cx={SUN_X} cy={SUN_Y} r="56" fill="url(#sunCore)" />
      <g stroke="#FFC34D" strokeWidth="3" strokeLinecap="round" opacity="0.85">
        <g transform={`translate(${SUN_X} ${SUN_Y})`}>
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0"
            to="360"
            dur="60s"
            repeatCount="indefinite"
            additive="sum"
          />
          {Array.from({ length: 12 }).map((_, i) => {
            const a = (i * 360) / 12;
            const rad = (a * Math.PI) / 180;
            return (
              <line
                key={i}
                x1={Math.cos(rad) * 70}
                y1={Math.sin(rad) * 70}
                x2={Math.cos(rad) * 92}
                y2={Math.sin(rad) * 92}
              />
            );
          })}
        </g>
      </g>

      {/* Light cone hint */}
      <path
        d={`M ${SUN_X + 56} ${SUN_Y - 8} L ${EARTH_X - R} ${EARTH_Y - 60} L ${EARTH_X - R} ${EARTH_Y + 60} L ${SUN_X + 56} ${SUN_Y + 8} Z`}
        fill="rgba(255, 220, 130, 0.06)"
      />

      {/* ── Earth ─────────────────────────────────── */}
      <circle cx={EARTH_X} cy={EARTH_Y} r={R} fill="url(#ocean)" />

      {/* Continents — translated to earth center, then rotated around 0,0.
          Counter-clockwise (visible motion: continents enter night on the right,
          exit on the left). 16 s per turn so it's clearly visible. */}
      <g clipPath="url(#earthClip)">
        <g transform={`translate(${EARTH_X} ${EARTH_Y})`}>
          <g>
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0"
              to="-360"
              dur="16s"
              repeatCount="indefinite"
            />
            {continents.map((c, i) => (
              <path
                key={i}
                d={c.d}
                fill={c.fill}
                stroke="rgba(0, 30, 60, 0.4)"
                strokeWidth="1.2"
              />
            ))}
          </g>
        </g>
      </g>

      <circle cx={EARTH_X} cy={EARTH_Y} r={R} fill="url(#dayGlow)" clipPath="url(#earthClip)" />

      {/* Day/Night terminator overlay (fixed) */}
      <circle cx={EARTH_X} cy={EARTH_Y} r={R} fill="url(#terminator)" />

      {/* City lights on night side */}
      <g clipPath="url(#nightHalf)">
        {lights.map((l, i) => (
          <circle
            key={i}
            cx={EARTH_X + l.dx}
            cy={EARTH_Y + l.dy}
            r="1.6"
            fill="#FFE9A6"
          >
            <animate
              attributeName="opacity"
              values="0.3;1;0.3"
              dur="3s"
              begin={l.delay}
              repeatCount="indefinite"
            />
          </circle>
        ))}
      </g>

      <circle cx={EARTH_X} cy={EARTH_Y} r={R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" />

      <g
        fontFamily='"Neo Sans", "Open Sans", sans-serif'
        fontSize="12"
        fontWeight="600"
        letterSpacing="2"
        textAnchor="middle"
        fill="rgba(255,255,255,0.8)"
        style={{ textTransform: "uppercase" }}
      >
        <text x={EARTH_X - R * 0.55} y={EARTH_Y + R + 28}>Tag</text>
        <text x={EARTH_X + R * 0.55} y={EARTH_Y + R + 28}>Nacht</text>
      </g>

      {/* Tiny moon orbiting */}
      <g>
        <animateTransform
          attributeName="transform"
          type="rotate"
          from={`0 ${EARTH_X} ${EARTH_Y}`}
          to={`360 ${EARTH_X} ${EARTH_Y}`}
          dur="36s"
          repeatCount="indefinite"
        />
        <circle cx={EARTH_X} cy={EARTH_Y - R - 22} r="6" fill="#E0E0E0" opacity="0.7" />
      </g>
    </svg>
  );
}
